import PropTypes from "prop-types";
import React, { useEffect, useState, useCallback } from "react";
import LendingPricingSvg from "../svg/LendingPricingSvg";
import CurrentPlanSvg from "../svg/CurrentPlanSvg";
import { URLs } from "../utils/apis";
import BackToFreePlanWindow from "../components/BackToFreePlanWindow";
import {
  paddleActivePlan,
  handleCheckout,
  handleInitPaddle,
  fetchBillingPlans,
  renderPlanButtonText,
  getPeriodLabel,
  getSubscriptionDetails,
  previewSubscriptionUpdate,
  updateSubscriptionQuantity,
  openPaymentUpdateCheckout,
} from "../utils/paddle";
import UpdateSubscriptionModal from "../components/UpdateSubscriptionModal";
import Loading from "../components/Loading";
import {
  Box,
  Container,
  Flex,
  Image,
  NumberInput,
  Text,
  Tooltip,
  useMantineTheme,
} from "@mantine/core";
import { useStylesPage } from "../utils/mantine/useStylesPage";
import { useStylesButton } from "../utils/mantine/useStylesButton";
import { useStylesLoader } from "../utils/mantine/useStylesLoader";
import { useStylesContainer } from "../utils/mantine/useStylesContainer";
import { useStylesInput } from "../utils/mantine/useStylesInput";
import CustomButton from "../components/mantineCustomComponents/CustomButton";
import { useStylesPagePricing } from "../utils/mantine/useStylesPagePricing";
import CustomAnchor from "../components/mantineCustomComponents/CustomAnchor";
import { useStylesLanding } from "../utils/mantine/useStylesLanding";

const PricingForm = ({ staticBasePath, djangoContext }) => {
  // Mantine theme instance to apply custom styles and theming throughout the component
  const theme = useMantineTheme();

  //css classes
  const { classes: classesPage } = useStylesPage();

  const { classes: buttonClasses } = useStylesButton();

  const { classes: containerClasses } = useStylesContainer();

  const { classes: loaderClasses } = useStylesLoader();

  const { classes: pricingPageClasses } = useStylesPagePricing();

  const { classes: landingClasses } = useStylesLanding();

  const { classes: inputClasses } = useStylesInput();

  // Billing Cycle can be month or year
  const [billingCycle, setBillingCycle] = useState("month");

  // We are waiting for the payment
  const [loading, setLoading] = useState(false);

  // Memoize the callback function to avoid unnecessary re-renders
  const handleLoading = useCallback((bool) => {
    setLoading(bool);
  }, []);

  // Get email from django
  const {
    userEmail,
    userId,
    subscriberId,
    paddleCustomerId,
    paddleOwnershipToken,
  } = djangoContext;

  // Set active plan
  const [activePlan, setActivePlan] = useState("Free");

  // Memoize the callback function to avoid unnecessary re-renders
  const handleActivePlan = useCallback((plan) => {
    setActivePlan(plan);
  }, []);

  // Current active period
  const [activePeriod, setActivePeriod] = useState("");

  // Memoize the callback function to avoid unnecessary re-renders
  const handleActivePeriod = useCallback((period) => {
    setActivePeriod(period);
  }, []);

  // Set all Plans
  const [allPlans, setAllPlans] = useState([]);

  // Memoize the callback function to avoid unnecessary re-renders
  const handleAllPlans = useCallback((plans) => {
    setAllPlans(plans);
  }, []);

  // Fetch plans and init payment
  useEffect(() => {
    // Get All Plans
    fetchBillingPlans(handleAllPlans);

    handleInitPaddle(handleLoading);
  }, []); // runs once on mount

  // Set active plan
  useEffect(() => {
    if (allPlans && allPlans.length > 0) {
      paddleActivePlan(handleActivePlan, handleActivePeriod, allPlans);
    }
  }, [allPlans]);

  // Fetch subscription details for special plan mode
  useEffect(() => {
    if (isSpecialPlanMode && allPlans.length > 0) {
      const fetchSubscriptionInfo = async () => {
        setLoadingSpecialPlanStatus(true);
        try {
          const allSubscriptions = await getSubscriptionDetails();
          // Check if subscribed to special plan (addon)
          const specialPlan = allPlans.find(
            (p) => p.slug === "hidden-1-live-plan",
          );

          if (specialPlan && Array.isArray(allSubscriptions)) {
            // Store price_id for update requests
            setSpecialPlanPriceId(specialPlan.price?.paddle_id);

            // Find ALL addon subscriptions and get the most recent one (by date)
            let latestAddonSubscription = null;
            let latestAddonItem = null;

            for (const subscription of allSubscriptions) {
              if (
                subscription.subscription_items &&
                subscription.subscription_items.length > 0
              ) {
                const subscribedItem = subscription.subscription_items.find(
                  (item) => item.price_id === specialPlan.price?.paddle_id,
                );
                if (subscribedItem) {
                  // Keep the most recent subscription (allSubscriptions is already sorted by -started_at from backend)
                  if (!latestAddonSubscription) {
                    latestAddonSubscription = subscription;
                    latestAddonItem = subscribedItem;
                  }
                }
              }
            }

            // Set state from the most recent addon subscription
            if (latestAddonSubscription && latestAddonItem) {
              setIsSubscribedToSpecialPlan(true);
              setCurrentQuantity(latestAddonItem.quantity);
              setNewQuantity(latestAddonItem.quantity);
            }
          }
        } catch (error) {
          console.error("Error fetching subscription info:", error);
        } finally {
          setLoadingSpecialPlanStatus(false);
        }
      };
      fetchSubscriptionInfo();
    } else if (isSpecialPlanMode && allPlans.length === 0) {
      // Still loading plans
      setLoadingSpecialPlanStatus(true);
    }
  }, [isSpecialPlanMode, allPlans]);

  // Show window cancel subscription
  const [backToFreeWindow, setBackToFreeWindow] = useState(false);

  // Special plan mode via query param
  const queryPlan =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("plan")
      : null;
  const isSpecialPlanMode = queryPlan === "1-live-index";

  // Subscription items and quantity management
  const [currentQuantity, setCurrentQuantity] = useState(1);
  const [newQuantity, setNewQuantity] = useState(1);
  const [isSubscribedToSpecialPlan, setIsSubscribedToSpecialPlan] =
    useState(false);
  const [loadingSpecialPlanStatus, setLoadingSpecialPlanStatus] =
    useState(false);
  const [specialPlanPriceId, setSpecialPlanPriceId] = useState(null);

  // Update subscription modal states
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [updateError, setUpdateError] = useState(null);
  const [paymentError, setPaymentError] = useState(false);
  const [paymentMethodUpdated, setPaymentMethodUpdated] = useState(false);
  const [paymentUpdateLoading, setPaymentUpdateLoading] = useState(false);

  // Decide if a plan should be rendered
  const shouldShowPlan = useCallback(
    (product) => {
      if (isSpecialPlanMode) {
        return product?.slug === "hidden-1-live-plan";
      }

      const billingInterval = product?.price?.billing_cycle_interval;
      const matchesCycle =
        billingInterval === billingCycle ||
        !product?.price ||
        billingInterval === "day" ||
        billingInterval === "week";

      return matchesCycle && product?.is_public;
    },
    [isSpecialPlanMode, billingCycle],
  );

  // Handle show window cancel subscription
  const handleClickBackToFreeWindow = () => {
    setBackToFreeWindow(true);
  };

  // Handle update subscription via Paddle Checkout
  const handlePreviewUpdate = async () => {
    if (newQuantity === currentQuantity) {
      return;
    }

    try {
      setUpdateLoading(true);
      setUpdateError(null);
      setPaymentError(false);
      const preview = await previewSubscriptionUpdate(
        newQuantity,
        specialPlanPriceId,
      );
      setPreviewData(preview);
      setShowUpdateModal(true);
    } catch (error) {
      console.error("Error previewing subscription update:", error);
      setUpdateError(error.message || "Failed to preview subscription update");
      setShowUpdateModal(true);
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleConfirmUpdate = async () => {
    try {
      setUpdateLoading(true);
      setUpdateError(null);
      setPaymentError(false);
      await updateSubscriptionQuantity(newQuantity, specialPlanPriceId);
      setUpdateSuccess(true);
      setCurrentQuantity(newQuantity);

      // Auto-close after 3 seconds
      setTimeout(() => {
        setShowUpdateModal(false);
        setUpdateSuccess(false);
        setPreviewData(null);
      }, 3000);
    } catch (error) {
      console.error("Error updating subscription:", error);
      setUpdateError(error.message || "Failed to update subscription");
      setPaymentError(error.payment_error || false);
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowUpdateModal(false);
    setPreviewData(null);
    setUpdateSuccess(false);
    setUpdateError(null);
    setPaymentError(false);
    setPaymentMethodUpdated(false);
  };

  const handleUpdatePayment = async () => {
    try {
      setPaymentUpdateLoading(true);

      await openPaymentUpdateCheckout(
        () => {
          // Success callback - after payment method updated
          setPaymentUpdateLoading(false);
          setPaymentMethodUpdated(true);
          setShowUpdateModal(true);

          // Auto-close after 3 seconds
          setTimeout(() => {
            handleCloseModal();
          }, 3000);
        },
        () => {
          // Checkout opened callback - close error modal when Paddle opens
          setPaymentUpdateLoading(false);
          setShowUpdateModal(false);
          setUpdateError(null);
          setPaymentError(false);
        },
      );
    } catch (error) {
      console.error("Error opening payment update:", error);
      setPaymentUpdateLoading(false);
      setUpdateError("Failed to open payment update");
      setShowUpdateModal(true);
    }
  };

  return (
    <>
      <title>vBase Verify App</title>
      <meta
        name="description"
        content="Use vBase Verify to quickly and easily verify stamps!"
      />
      <meta property="og:title" content="vBase Verify App" />
      <meta
        property="og:description"
        content="Use vBase Verify to quickly and easily verify stamps!"
      />
      <Flex
        className={`${classesPage.page} ${pricingPageClasses.pricing} main-content`}
        direction="column"
        align={theme.flex.align.center}
      >
        <Container className={containerClasses.container}>
          {loading && (
            <Loading
              title="We are contacting your bank, this may take a few moments..."
              text="Can take 20 seconds, but usually faster!"
            />
          )}

          {backToFreeWindow && (
            <BackToFreePlanWindow
              setBackToFreeWindow={setBackToFreeWindow}
              backToFreeWindow={backToFreeWindow}
            />
          )}

          <Box className={pricingPageClasses.pricingHeader}>
            {isSpecialPlanMode ? (
              <Text className={classesPage.headerTitleSpecial}></Text>
            ) : (
              <Text className={classesPage.headerTitle}>
                Select Your Best Fit
              </Text>
            )}
          </Box>

          {!isSpecialPlanMode && (
            <Flex
              align={theme.flex.align.center}
              gap={theme.flex.gap.xs}
              justify={theme.flex.justify.center}
            >
              <Flex
                className={`${buttonClasses.tabs} ${pricingPageClasses.pricingTabs}`}
              >
                <CustomButton
                  id="monthly"
                  text="Monthly"
                  onClick={() => setBillingCycle("month")}
                  componentClasses={billingCycle === "month" ? "active" : ""}
                />

                <Tooltip
                  className={pricingPageClasses.pricingTooltip}
                  arrowOffset={14}
                  arrowSize={8}
                  label="Save 20%"
                  withArrow
                  opened
                  position="top-start"
                >
                  <Box
                    className={pricingPageClasses.pricingTooltipContainer}
                  ></Box>
                </Tooltip>
                <CustomButton
                  id="year"
                  text="Yearly"
                  onClick={() => setBillingCycle("year")}
                  componentClasses={billingCycle === "year" ? "active" : ""}
                ></CustomButton>
              </Flex>
            </Flex>
          )}

          {!isSpecialPlanMode && (
            <Flex justify={theme.flex.justify.center}>
              <Flex
                className={landingClasses.pricingDiy}
                justify={theme.flex.justify.center}
                wrap="wrap"
              >
                <Image
                  src={`${staticBasePath}public/images/svg/diy.svg`}
                  alt="DIY"
                />
                <Text component="span">DIY:</Text>
                <Text>unlimited free stamps and validations forever.</Text>
                <CustomAnchor
                  text="Always an option."
                  componentClasses=""
                  target="_blank"
                  href="https://docs.vbase.com/"
                  rel="noreferrer"
                />
              </Flex>
            </Flex>
          )}

          <Flex
            className={`${landingClasses.pricingBlock} ${pricingPageClasses.pricingBlock}`}
            justify={
              isSpecialPlanMode
                ? theme.flex.justify.center
                : theme.flex.justify.between
            }
            wrap="wrap"
          >
            {allPlans
              .sort((a, b) => a.sort_order - b.sort_order)
              .filter((product) => product?.slug !== "free-plan-ext")
              .map(
                (product) =>
                  shouldShowPlan(product) && (
                    <Flex
                      key={product.slug}
                      className={`${landingClasses.pricingBlockItem} ${
                        pricingPageClasses.pricingBlockItem
                      } ${landingClasses.pricingBlockItemSecond}

                        ${(
                          product?.price?.custom_data?.product_type || "custom"
                        ).toLowerCase()}-plan
                  ${
                    product?.is_free
                      ? pricingPageClasses.pricingBlockItemFirst
                      : ""
                  }
    ${
      product?.price?.custom_data?.product_type === "Base"
        ? pricingPageClasses.pricingBlockItemSecond
        : ""
    }
    ${
      product?.price?.custom_data?.product_type === "Pro"
        ? `${pricingPageClasses.pricingBlockItemThird} ${landingClasses.pricingBlockItemThird}`
        : ""
    }
                    ${
                      activePlan ===
                        product?.price?.custom_data?.product_type ||
                      (activePlan === "Free" && product?.is_free)
                        ? pricingPageClasses.pricingBlockItemActive
                        : ""
                    }

                        `}
                    >
                      <Flex
                        className={`${landingClasses.pricingBlockItemHead} ${
                          pricingPageClasses.pricingBlockItemHead
                        } ${
                          isSpecialPlanMode &&
                          landingClasses.pricingBlockItemHeadCustom
                        }`}
                        justify={theme.flex.justify.between}
                        wrap="wrap"
                      >
                        <Box
                          className={`${
                            landingClasses.pricingBlockItemHeadTitle
                          } ${
                            isSpecialPlanMode
                              ? landingClasses.pricingBlockItemHeadTitleCustom
                              : ""
                          }`}
                        >
                          {isSpecialPlanMode
                            ? "vBase INDEX"
                            : product?.is_free
                            ? "Free"
                            : product?.price?.custom_data?.product_type}
                        </Box>
                        <Box
                          className={`${
                            pricingPageClasses.pricingBlockItemHeadSubtitle
                          } ${
                            isSpecialPlanMode
                              ? landingClasses.pricingBlockItemHeadSubtitleCustom
                              : ""
                          }`}
                        >
                          {product?.price?.custom_data?.price_month}
                          {isSpecialPlanMode && "/month"}
                          {!product?.is_free && !isSpecialPlanMode && (
                            <Text component="span">/mo</Text>
                          )}
                        </Box>
                        {!isSpecialPlanMode && (
                          <>
                            <Box
                              className={
                                pricingPageClasses.pricingBlockItemHeadWrapBreak
                              }
                            ></Box>

                            <Flex
                              justify={theme.flex.justify.center}
                              className={
                                pricingPageClasses.pricingBlockItemHeadWrap
                              }
                            >
                              {(activePlan ===
                                product?.price?.custom_data?.product_type ||
                                (product?.is_free &&
                                  activePlan === "Free")) && (
                                <Box
                                  className={
                                    pricingPageClasses.pricingBlockItemHeadWrapCurrent
                                  }
                                >
                                  Current Plan
                                  {!product?.is_free && (
                                    <>/{getPeriodLabel(activePeriod)}</>
                                  )}
                                </Box>
                              )}

                              <Box
                                className={
                                  pricingPageClasses.pricingBlockItemHeadSubTitleYear
                                }
                              >
                                &nbsp;
                                {billingCycle === "year" && (
                                  <>{product?.price?.custom_data?.price_year}</>
                                )}
                              </Box>
                            </Flex>
                          </>
                        )}
                      </Flex>
                      <Box className={landingClasses.pricingBlockItemBody}>
                        {isSpecialPlanMode ? (
                          <>
                            <Flex
                              className={
                                landingClasses.pricingBlockItemBodyItem
                              }
                            >
                              <Box
                                className={
                                  landingClasses.pricingBlockItemBodyItemTextCustom
                                }
                              >
                                1 Live vBase Index
                              </Box>
                            </Flex>
                            <Flex
                              className={
                                landingClasses.pricingBlockItemBodyItem
                              }
                            >
                              <LendingPricingSvg />
                              <Box
                                className={
                                  landingClasses.pricingBlockItemBodyItemText
                                }
                              >
                                Daily performance computation
                              </Box>
                            </Flex>
                            <Flex
                              className={
                                landingClasses.pricingBlockItemBodyItem
                              }
                            >
                              <LendingPricingSvg />
                              <Box
                                className={
                                  landingClasses.pricingBlockItemBodyItemText
                                }
                              >
                                Blockchain verification
                              </Box>
                            </Flex>
                            <Flex
                              className={
                                landingClasses.pricingBlockItemBodyItem
                              }
                            >
                              <LendingPricingSvg />
                              <Box
                                className={
                                  landingClasses.pricingBlockItemBodyItemText
                                }
                              >
                                Live dashboard
                              </Box>
                            </Flex>
                          </>
                        ) : product?.is_free ? (
                          <>
                            <Flex
                              className={
                                landingClasses.pricingBlockItemBodyItem
                              }
                            >
                              <LendingPricingSvg />
                              <Box
                                className={
                                  landingClasses.pricingBlockItemBodyItemText
                                }
                              >
                                100 stamps per month
                              </Box>
                            </Flex>

                            <Flex
                              className={
                                landingClasses.pricingBlockItemBodyItem
                              }
                            >
                              <LendingPricingSvg />
                              <Box
                                className={
                                  landingClasses.pricingBlockItemBodyItemText
                                }
                              >
                                Unlimited validations*
                              </Box>
                            </Flex>
                            <Flex
                              className={
                                landingClasses.pricingBlockItemBodyItem
                              }
                            >
                              <LendingPricingSvg />
                              <Box
                                className={
                                  landingClasses.pricingBlockItemBodyItemText
                                }
                              >
                                E-mail support
                              </Box>
                            </Flex>

                            <Flex
                              className={
                                landingClasses.pricingBlockItemBodyItem
                              }
                            >
                              <LendingPricingSvg />
                              <Box
                                className={
                                  landingClasses.pricingBlockItemBodyItemText
                                }
                              >
                                1 live index
                              </Box>
                            </Flex>
                          </>
                        ) : product?.price?.custom_data?.product_type ===
                          "Base" ? (
                          <>
                            <Flex
                              className={
                                landingClasses.pricingBlockItemBodyItem
                              }
                            >
                              <LendingPricingSvg />
                              <Box
                                className={
                                  landingClasses.pricingBlockItemBodyItemText
                                }
                              >
                                All features of FREE plan
                              </Box>
                            </Flex>

                            <Flex
                              className={
                                landingClasses.pricingBlockItemBodyItem
                              }
                            >
                              <LendingPricingSvg />
                              <Box
                                className={
                                  landingClasses.pricingBlockItemBodyItemText
                                }
                              >
                                400 stamps per month
                              </Box>
                            </Flex>

                            <Flex
                              className={
                                landingClasses.pricingBlockItemBodyItem
                              }
                            >
                              <LendingPricingSvg />
                              <Box
                                className={
                                  landingClasses.pricingBlockItemBodyItemText
                                }
                              >
                                White glove support
                              </Box>
                            </Flex>

                            <Flex
                              className={
                                landingClasses.pricingBlockItemBodyItem
                              }
                            >
                              <LendingPricingSvg />
                              <Box
                                className={
                                  landingClasses.pricingBlockItemBodyItemText
                                }
                              >
                                3 live indices
                              </Box>
                            </Flex>
                          </>
                        ) : (
                          <>
                            <Flex
                              className={
                                landingClasses.pricingBlockItemBodyItem
                              }
                            >
                              <LendingPricingSvg />
                              <Box
                                className={
                                  landingClasses.pricingBlockItemBodyItemText
                                }
                              >
                                All features of BASE plan
                              </Box>
                            </Flex>

                            <Flex
                              className={
                                landingClasses.pricingBlockItemBodyItem
                              }
                            >
                              <LendingPricingSvg />
                              <Box
                                className={
                                  landingClasses.pricingBlockItemBodyItemText
                                }
                              >
                                2000 stamps per month
                              </Box>
                            </Flex>

                            <Flex
                              className={
                                landingClasses.pricingBlockItemBodyItem
                              }
                            >
                              <LendingPricingSvg />
                              <Box
                                className={
                                  landingClasses.pricingBlockItemBodyItemText
                                }
                              >
                                Managed service for S3, REST, & other data
                                stores
                              </Box>
                            </Flex>

                            <Flex
                              className={
                                landingClasses.pricingBlockItemBodyItem
                              }
                            >
                              <LendingPricingSvg />
                              <Box
                                className={
                                  landingClasses.pricingBlockItemBodyItemText
                                }
                              >
                                5 live indices
                              </Box>
                            </Flex>
                          </>
                        )}
                      </Box>
                      <Flex
                        justify={theme.flex.justify.center}
                        className={landingClasses.pricingBlockItemFooter}
                      >
                        {(() => {
                          // Calculate display conditions for better readability
                          const isLoadingSpecialPlan =
                            isSpecialPlanMode && loadingSpecialPlanStatus;
                          const shouldShowSpecialPlanUI =
                            isSpecialPlanMode && isSubscribedToSpecialPlan;
                          const isCurrentPlan =
                            (activePlan === "Free" && product?.is_free) ||
                            (activePlan ===
                              product?.price?.custom_data?.product_type &&
                              billingCycle === activePeriod);

                          if (isLoadingSpecialPlan) {
                            return (
                              <Flex
                                justify={theme.flex.justify.center}
                                align={theme.flex.align.center}
                                style={{ padding: "20px" }}
                              >
                                <Text size="sm" color="dimmed">
                                  Loading...
                                </Text>
                              </Flex>
                            );
                          }

                          if (shouldShowSpecialPlanUI) {
                            return (
                              <Flex
                                className={
                                  pricingPageClasses.changeSubscribeBlock
                                }
                              >
                                <Box>
                                  <Text
                                    classNames={{ root: "modal-text-unpinned" }}
                                  >
                                    Current: {currentQuantity} index
                                    {currentQuantity !== 1 ? "es" : ""}
                                  </Text>
                                  <NumberInput
                                    value={newQuantity}
                                    onChange={setNewQuantity}
                                    min={1}
                                    max={100}
                                    label="Number of indexes"
                                    placeholder="Enter quantity"
                                    classNames={{
                                      root: inputClasses.number,
                                    }}
                                  />
                                </Box>
                                <Flex
                                  justify={theme.flex.justify.center}
                                  align={theme.flex.align.center}
                                  className={landingClasses.pricingBlockButtons}
                                >
                                  <CustomButton
                                    text={
                                      updateLoading
                                        ? "Loading..."
                                        : "Update Subscription"
                                    }
                                    componentClasses={`${buttonClasses.buttonBlack} ${landingClasses.pricingBlockButtonsButton} ${pricingPageClasses.pricingBlockButtonsButton}`}
                                    onClick={handlePreviewUpdate}
                                    disabled={
                                      newQuantity === currentQuantity ||
                                      updateLoading
                                    }
                                  />
                                </Flex>
                              </Flex>
                            );
                          }

                          if (isCurrentPlan) {
                            return <CurrentPlanSvg />;
                          }

                          return (
                            <Flex
                              justify={theme.flex.justify.center}
                              align={theme.flex.align.center}
                              className={landingClasses.pricingBlockButtons}
                            >
                              <CustomButton
                                text={
                                  isSpecialPlanMode
                                    ? "Subscribe Now"
                                    : renderPlanButtonText(
                                        product?.price?.custom_data
                                          ?.product_type,
                                        activePeriod,
                                        activePlan,
                                      )
                                }
                                componentClasses={`

 ${isSpecialPlanMode && buttonClasses.buttonGreenHover}
                              ${
                                product?.price?.custom_data?.product_type ===
                                  "Base" || product?.is_free
                                  ? buttonClasses.buttonWhite
                                  : ""
                              }
                              ${
                                product?.price?.custom_data?.product_type ===
                                "Pro"
                                  ? buttonClasses.buttonBlack
                                  : ""
                              }
                               ${landingClasses.pricingBlockButtonsButton} ${
                                  pricingPageClasses.pricingBlockButtonsButton
                                }`}
                                onClick={
                                  product?.is_free
                                    ? () => handleClickBackToFreeWindow()
                                    : () =>
                                        handleCheckout(
                                          product?.price?.paddle_id,
                                          userEmail,
                                          userId,
                                          subscriberId,
                                          paddleCustomerId,
                                          paddleOwnershipToken,
                                        )
                                }
                              />
                            </Flex>
                          );
                        })()}
                      </Flex>
                    </Flex>
                  ),
              )}
          </Flex>
          {!isSpecialPlanMode && (
            <Flex className={pricingPageClasses.pricingCustom}>
              <Box className={pricingPageClasses.pricingCustomItemFirst}>
                <Box className={pricingPageClasses.pricingCustomItemTitle}>
                  Need Custom Pricing?
                </Box>
                <Flex
                  justify={theme.flex.justify.start}
                  align={theme.flex.align.center}
                  className={`${landingClasses.pricingBlockButtons} ${pricingPageClasses.pricingCustomButtons}`}
                >
                  <CustomAnchor
                    text="Contact Us"
                    target="_blank"
                    rel="noreferrer"
                    href={URLs.CONTACTS_URL}
                    componentClasses={`${buttonClasses.buttonBlack} ${landingClasses.pricingBlockButtonsButton}`}
                  />
                </Flex>
              </Box>
              <Box className={pricingPageClasses.pricingCustomItemSecond}>
                <Box className={pricingPageClasses.pricingCustomItemMobile}>
                  <Text component="span">Please Contact</Text>
                  <CustomAnchor
                    text="support@vbase.com"
                    href="mailto:support@vbase.com"
                    componentClasses="link"
                  />
                </Box>
                <Flex className={pricingPageClasses.pricingCustomItemLine}>
                  <LendingPricingSvg />
                  <Box className={pricingPageClasses.pricingCustomItemLineText}>
                    <Text component="b">Customized Solutions:</Text> Tailored
                    features, integrations, and support
                  </Box>
                </Flex>
                <Flex className={pricingPageClasses.pricingCustomItemLine}>
                  <LendingPricingSvg />
                  <Box className={pricingPageClasses.pricingCustomItemLineText}>
                    <Text component="b">Priority Support:</Text> 24/7 dedicated
                    support with a personal account manager
                  </Box>
                </Flex>
                <Flex className={pricingPageClasses.pricingCustomItemLine}>
                  <LendingPricingSvg />
                  <Box className={pricingPageClasses.pricingCustomItemLineText}>
                    <Text component="b">Dedicated Infrastructure:</Text>{" "}
                    Enhanced performance and scaled usage allowances
                  </Box>
                </Flex>
              </Box>
              <Box>
                <Image
                  className={pricingPageClasses.pricingCustomItemImg}
                  src={`${staticBasePath}public/images/png/custom-plan.png`}
                  alt="Custom Plan"
                />
              </Box>
            </Flex>
          )}
        </Container>
      </Flex>

      {loading && <Box className={loaderClasses.bg}></Box>}

      {/* Update Subscription Modal */}
      <UpdateSubscriptionModal
        opened={showUpdateModal}
        onClose={handleCloseModal}
        previewData={previewData}
        onConfirm={handleConfirmUpdate}
        loading={updateLoading}
        success={updateSuccess}
        error={updateError}
        paymentError={paymentError}
        onUpdatePayment={handleUpdatePayment}
        paymentMethodUpdated={paymentMethodUpdated}
        paymentUpdateLoading={paymentUpdateLoading}
      />
    </>
  );
};
PricingForm.propTypes = {
  staticBasePath: PropTypes.string.isRequired,
  djangoContext: PropTypes.object.isRequired,
};
export default PricingForm;
